import { CONFIG } from "../config";
import { mulberry32 } from "../rng";
import type { NoteEvent } from "../types";
import {
  activeSpan,
  chainProgress,
  extractPassages,
  extractProgress,
  initCoachState,
  step,
  viewCommands,
} from "./coach";
import {
  type BarProgress,
  type CoachConfig,
  DEFAULT_COACH_CONFIG,
  type RepResult,
} from "./coach-params";
import type {
  CoachCommands,
  CoachContext,
  CoachInput,
  CoachResume,
  CoachState,
} from "./coach-state";
import type { Engine } from "./engine";
import type { Dimension } from "./gate-policy";

// Thin engine-coupled wrapper around the pure Coach core (coach.ts). Each frame
// it snapshots the Engine into a bar-indexed CoachInput, steps the reducer, and
// applies the resulting commands (mapping bar indices → seconds via Song.bars).
// The Engine stays the executor; this owns no playback logic of its own.

export class CoachDriver {
  private state: CoachState;
  /** Edge latch so a rep-end is handled once, not every clamped frame. */
  private handledEnd = false;
  private readonly rng: () => number;
  /** Per-bar "last earned a mastery rep" stamps (epoch ms), for per-bar decay.
   *  Kept here, not in the pure reducer, so the reducer stays clock-free. */
  private readonly practicedAt: number[];
  status = "";
  /** Active mastery dimensions (Scorecard: proficiency ∪ lesson focus), set by the
   *  app when the song loads. Undefined ⇒ today's fixed three gates. */
  activeGates: ReadonlySet<Dimension> | undefined;
  /** Effective on-time pass-rate (%) for the timing gate, ramped with proficiency by the
   *  app on load (see main.ts). Undefined ⇒ the config's flat masteryTimingPct. */
  timingPct: number | undefined;
  /** Piece baseline highway opacity (the reading crutch level), set by the app on load.
   *  Drives the feed depth; 1 (default) makes the feed inert (non-reading). */
  baselineOpacity = 1;
  /** Effective crutch opacity the app overlays while coaching — the fed value, or the
   *  baseline when not feeding. */
  highwayOpacity = 1;

  constructor(
    private readonly engine: Engine,
    /** Song.bars: barline times in seconds, length = barCount + 1. */
    private readonly bars: number[],
    private readonly config: CoachConfig = DEFAULT_COACH_CONFIG,
    seed = 1,
    resume?: CoachResume,
    /** Persistence clock (injected for tests); not a simulation timebase. */
    private readonly now: () => number = Date.now,
  ) {
    this.rng = mulberry32(seed);
    const barCount = Math.max(1, bars.length - 1);
    this.practicedAt = Array.from(
      { length: barCount },
      (_, i) => resume?.bars?.[i]?.lastPracticedMs ?? 0,
    );
    this.state = initCoachState(barCount, config, this.rng, resume);
    this.apply(viewCommands(this.state));
  }

  /** Resumable per-bar mastery snapshot (for store/coach-progress.ts). */
  progress(): BarProgress[] {
    return extractProgress(this.state).map((b, i) => {
      const at = this.practicedAt[i];
      return at ? { ...b, lastPracticedMs: at } : b;
    });
  }

  /** Full resumable context: per-bar mastery PLUS the live passage layout
   *  (tempo/phase/window/active), so a re-attach continues the tempo ramp where it
   *  left off instead of dropping back to the floor. */
  snapshot(): CoachContext {
    return { bars: this.progress(), ...extractPassages(this.state) };
  }

  /** Per-chain mastery for the top-bar dots (one entry per passage). */
  chainProgress(): { fraction: number; done: boolean }[] {
    return chainProgress(this.state);
  }

  /** Call once per frame after `engine.update(...)`. */
  update(): void {
    const atEnd = this.engine.windowEnd !== null && this.engine.playhead >= this.engine.windowEnd;
    const reachedEnd = atEnd && !this.handledEnd;
    this.handledEnd = atEnd;

    // Zero key-presses in the current pass (the scorer is reset per pass) ⇒ the
    // player isn't playing. The reducer earns no penalty for such a pass; a
    // *completed* one (reachedEnd) latches `idle` so the host can pause the loop.
    const sc = this.engine.scoreStats;
    const noInput = sc.perfect + sc.good + sc.wrong === 0;

    const before = extractProgress(this.state);
    const input: CoachInput = {
      bar: this.barAt(this.engine.playhead),
      newMistakes: this.engine.takeMistakes(),
      reachedEnd,
      notesInSpan: this.notesInSpan(),
      rng: this.rng,
      repResult: reachedEnd ? this.repResult() : undefined,
      noInput,
      activeGates: this.activeGates,
      timingPct: this.timingPct,
      baselineOpacity: this.baselineOpacity,
    };
    const result = step(this.state, input, this.config);
    this.state = result.state;
    // Stamp any bar that just earned a mastery rep (a clean run-through credits it).
    const after = extractProgress(this.state);
    for (let i = 0; i < after.length; i++) {
      if ((after[i]?.masteryReps ?? 0) > (before[i]?.masteryReps ?? 0))
        this.practicedAt[i] = this.now();
    }
    this.apply(result.commands);
    // A completed pass with no input → step away detected; hold the status until a
    // key resumes (the host pauses playback while `idle`).
    if (reachedEnd) this.idle = noInput;
    if (this.idle) this.status = "Coach paused — play a note to resume";
  }

  /** True when the last completed pass had zero key-presses (player stepped away).
   *  The host pauses the loop and clears it via {@link clearIdle} on the next key. */
  idle = false;

  /** Resume after an idle auto-pause (called when the player plays again). */
  clearIdle(): void {
    this.idle = false;
  }

  get done(): boolean {
    return this.state.done;
  }

  private apply(c: CoachCommands): void {
    if (c.setTempo !== undefined) this.engine.tempo = c.setTempo;
    this.highwayOpacity = c.highwayOpacity ?? this.baselineOpacity;
    if (c.setWindowBars) {
      // Clamp the window end to the song's playable end. A final bar can be padded
      // past its last note (the bar grid runs to the next barline), so the barline
      // time can sit beyond `duration`. Scored mode halts the playhead at the
      // duration, so a window end past it is never reached — the rep never completes
      // and the Coach stalls (the five-finger "8/9 then frozen" field bug). Keeping
      // the window within content makes windowEnd reachable, so the drill loops.
      const end = Math.min(this.barTime(c.setWindowBars[1]), this.engine.duration);
      this.engine.setWindow(this.barTime(c.setWindowBars[0]), end);
    }
    if (c.seekToBar !== undefined) {
      const t = this.barTime(c.seekToBar);
      this.engine.seekTo(t - CONFIG.lookaheadSeconds, t);
      this.handledEnd = false; // moved off the window end
    }
    this.status = c.status;
  }

  private barTime(bar: number): number {
    const i = Math.max(0, Math.min(bar, this.bars.length - 1));
    return this.bars[i] ?? 0;
  }

  private barAt(sec: number): number {
    // Before the first bar (the lead-in) the contract is −1, not bar 0 — else a lead-in
    // mistake would be charged to bar 0 and skew weakest-bar selection.
    if (sec < (this.bars[0] ?? 0)) return -1;
    let i = 0;
    for (let k = 0; k < this.bars.length - 1; k++) {
      if ((this.bars[k] ?? 0) <= sec) i = k;
      else break;
    }
    return i;
  }

  /** The just-finished rep's tallies (the scorer is reset per rep, so its current
   *  stats ARE this rep's). `onTime` counts hits within the mastery timing
   *  tolerance — at the default 120ms (= the hit window) that's every hit, so the
   *  timing gate is non-constraining (degenerate), and tightening it bites. */
  private repResult(): RepResult {
    const s = this.engine.scoreStats;
    const played = s.perfect + s.good;
    const onTime = this.engine.onTimeHits(this.config.masteryTimingMs / 1000);
    const { dynamics, articulation } = this.engine.expressionScores();
    return { expected: played + s.misses, played, wrong: s.wrong, onTime, dynamics, articulation };
  }

  private spanNotesMemo: { notes: readonly NoteEvent[]; lo: number; hi: number; n: number } | null =
    null;

  // Counts notes in the active span. Called every frame to build CoachInput but only read in
  // the run-through abort branch; the span changes only when the coach re-aims, so memoize on
  // (notes identity, span) to avoid an O(notes) rescan on every drill frame.
  private notesInSpan(): number {
    const [lo, hi] = activeSpan(this.state);
    const notes = this.engine.notes;
    const m = this.spanNotesMemo;
    if (m && m.notes === notes && m.lo === lo && m.hi === hi) return m.n;
    const a = this.barTime(lo);
    const b = this.barTime(hi);
    let n = 0;
    for (const note of notes) {
      if (note.onset >= a && note.onset < b) n++;
    }
    this.spanNotesMemo = { notes, lo, hi, n };
    return n;
  }
}
